"use client";

/**
 * ToolActions - Action buttons for tool displays
 *
 * Provides copy, expand/collapse, retry, and other actions
 * for tool result displays.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  ContentCopy,
  Check,
  ExpandMore,
  ExpandLess,
  Refresh,
  MoreVert,
  Code,
  Download,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { Tooltip } from "@/ui";
import { colors, spacing, radius, textStyles, shadows, primitives } from "@/ui/tokens";
import type { ToolStatus } from "@/lib/sola/types/tools";
import { IconButton } from "./IconButton";

export interface ToolActionsProps {
  /** Current tool status */
  status: ToolStatus;
  /** Whether the tool content is expanded */
  isExpanded?: boolean;
  /** Callback when expand/collapse is toggled */
  onToggleExpand?: () => void;
  /** Callback to retry the tool execution */
  onRetry?: () => void;
  /** Data to copy (as JSON) */
  copyData?: unknown;
  /** Whether to show in compact mode (icon-only) */
  compact?: boolean;
  /** Show full screen toggle */
  showFullscreen?: boolean;
  /** Whether currently in fullscreen */
  isFullscreen?: boolean;
  /** Callback for fullscreen toggle */
  onToggleFullscreen?: () => void;
  /** Show raw JSON view toggle */
  showRawToggle?: boolean;
  /** Whether showing raw JSON */
  isRawView?: boolean;
  /** Callback for raw view toggle */
  onToggleRaw?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * Simple dropdown menu item
 */
function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[2],
        width: "100%",
        padding: `${spacing[2]}px ${spacing[3]}px`,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        ...textStyles.body2,
        color: colors.text.primary,
        textAlign: "left",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = primitives.neutral[100];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      <span style={{ display: "flex", color: colors.text.muted, fontSize: 18 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

export function ToolActions({
  status,
  isExpanded = true,
  onToggleExpand,
  onRetry,
  copyData,
  compact = false,
  showFullscreen = false,
  isFullscreen = false,
  onToggleFullscreen,
  showRawToggle = false,
  isRawView = false,
  onToggleRaw,
  className,
}: ToolActionsProps) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const canRetry = status === "error" || status === "complete";
  const hasCopyData = copyData !== undefined && copyData !== null;

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleCopy = useCallback(async () => {
    if (!hasCopyData) return;

    try {
      const text =
        typeof copyData === "string"
          ? copyData
          : JSON.stringify(copyData, null, 2);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [copyData, hasCopyData]);

  const handleDownload = useCallback(() => {
    if (!hasCopyData) return;

    try {
      const text =
        typeof copyData === "string"
          ? copyData
          : JSON.stringify(copyData, null, 2);
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tool-result-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download:", err);
    }
    setMenuOpen(false);
  }, [copyData, hasCopyData]);

  // Compact mode with overflow menu
  if (compact) {
    return (
      <div className={className} style={{ display: "flex", alignItems: "center", position: "relative" }}>
        {onToggleExpand && (
          <Tooltip content={isExpanded ? "Collapse" : "Expand"}>
            <IconButton onClick={onToggleExpand}>
              {isExpanded ? (
                <ExpandLess style={{ fontSize: 18 }} />
              ) : (
                <ExpandMore style={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip content="More actions">
          <IconButton
            ref={menuButtonRef}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <MoreVert style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {menuOpen && (
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              zIndex: 100,
              minWidth: 180,
              backgroundColor: colors.canvas.default,
              borderRadius: radius.md,
              boxShadow: shadows[3],
              border: `1px solid ${colors.border.subtle}`,
              padding: `${spacing[1]}px 0`,
              overflow: "hidden",
            }}
          >
            {hasCopyData && (
              <MenuItem
                icon={copied ? <Check style={{ fontSize: 20 }} /> : <ContentCopy style={{ fontSize: 20 }} />}
                label={copied ? "Copied!" : "Copy result"}
                onClick={() => {
                  handleCopy();
                  setMenuOpen(false);
                }}
              />
            )}

            {hasCopyData && (
              <MenuItem
                icon={<Download style={{ fontSize: 20 }} />}
                label="Download JSON"
                onClick={handleDownload}
              />
            )}

            {showRawToggle && onToggleRaw && (
              <MenuItem
                icon={<Code style={{ fontSize: 20 }} />}
                label={isRawView ? "Show formatted" : "Show raw JSON"}
                onClick={() => {
                  onToggleRaw();
                  setMenuOpen(false);
                }}
              />
            )}

            {(hasCopyData || showRawToggle) && (onRetry || showFullscreen) && (
              <hr
                style={{
                  margin: `${spacing[1]}px 0`,
                  border: "none",
                  borderTop: `1px solid ${colors.border.subtle}`,
                }}
              />
            )}

            {onRetry && canRetry && (
              <MenuItem
                icon={<Refresh style={{ fontSize: 20 }} />}
                label="Retry"
                onClick={() => {
                  onRetry();
                  setMenuOpen(false);
                }}
              />
            )}

            {showFullscreen && onToggleFullscreen && (
              <MenuItem
                icon={
                  isFullscreen ? (
                    <FullscreenExit style={{ fontSize: 20 }} />
                  ) : (
                    <Fullscreen style={{ fontSize: 20 }} />
                  )
                }
                label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={() => {
                  onToggleFullscreen();
                  setMenuOpen(false);
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Full mode with individual buttons
  return (
    <div
      className={className}
      style={{ display: "flex", alignItems: "center", gap: spacing[1] }}
    >
      {/* Copy button */}
      {hasCopyData && (
        <Tooltip content={copied ? "Copied!" : "Copy result"}>
          <IconButton onClick={handleCopy}>
            {copied ? (
              <Check style={{ fontSize: 18, color: colors.signal.success }} />
            ) : (
              <ContentCopy style={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* Raw JSON toggle */}
      {showRawToggle && onToggleRaw && (
        <Tooltip content={isRawView ? "Show formatted" : "Show raw JSON"}>
          <IconButton
            onClick={onToggleRaw}
            style={isRawView ? { color: colors.action.product } : undefined}
          >
            <Code style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Fullscreen toggle */}
      {showFullscreen && onToggleFullscreen && (
        <Tooltip content={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
          <IconButton onClick={onToggleFullscreen}>
            {isFullscreen ? (
              <FullscreenExit style={{ fontSize: 18 }} />
            ) : (
              <Fullscreen style={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* Retry button */}
      {onRetry && canRetry && (
        <Tooltip content="Retry">
          <IconButton onClick={onRetry}>
            <Refresh style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Expand/Collapse button */}
      {onToggleExpand && (
        <Tooltip content={isExpanded ? "Collapse" : "Expand"}>
          <IconButton onClick={onToggleExpand}>
            {isExpanded ? (
              <ExpandLess style={{ fontSize: 18 }} />
            ) : (
              <ExpandMore style={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}

export default ToolActions;
